import { useAuth } from '@teko/auth-react';
import {
  Body,
  Button,
  Col,
  Row,
  Shape,
  styled,
  useModal,
  useTheme,
} from '@teko/ds-desktop';
import { AddAlternativeOutlined } from '@teko/ds-icon';
import { ThemeProps } from '@teko/ui';
import { Address } from '@teko/user';
import { Spinner } from 'components/desktop/atoms';
import { MODE_TERMINAL_PICKER, useDistanceTerminalContext } from 'contexts';
import { useAddresses } from 'hooks';
import { t } from 'i18n';
import { useMemo, useState } from 'react';
import { LocationPickerItem } from '../desktop/ModeListAddress/LocationPickerItem';
import { useGetDistanceTerminal } from '../useGetDistanceTerminal';
import { convertFullAddress } from '../utils';

export const ModeListAddress = () => {
  const {
    location,
    messageError,
    isLoadingGetDistanceTerminals,
    setModeTerminalPicker,
    setEditingAddress,
    resetSelectedAddress,
  } = useDistanceTerminalContext();
  const { user } = useAuth();
  const [selectedLocation, setSelectedLocation] =
    useState<typeof locations[0]>();

  const { addresses, loading: loadingAddress, deleteAddress } = useAddresses();

  const { getDistanceTerminalsByAddress } = useGetDistanceTerminal();

  const theme = useTheme();
  const { showModal } = useModal();

  const handleEditAddress = async (address?: Address) => {
    setEditingAddress(address);
    setModeTerminalPicker(MODE_TERMINAL_PICKER.ENTER_ADDRESS);
  };

  const handleRemoveAddress = (addressId: string) => {
    showModal({
      // BottomSheet in PopupTerminalPickerMobile renders with zIndex={2000}, so the
      // confirm modal needs a higher zIndex to appear above it instead of underneath.
      zIndex: 2100,
      title: t('Xác nhận'),
      children: <p>{t('Bạn có muốn xóa địa chỉ này?')}</p>,
      okText: t('Xóa'),
      cancelText: t('Hủy'),
      onOk: async () => {
        await deleteAddress(addressId);
        if (selectedLocation?.addressId === addressId) {
          setSelectedLocation(undefined);
        }
        if (location?.addressId === addressId) {
          resetSelectedAddress?.();
        }
      },
    });
  };

  const locations =
    useMemo(
      () =>
        addresses
          .map((a) => {
            return {
              addressId: a.addressId,
              address: a.address || '',
              wardName: a.wardName || '',
              provinceName: a.provinceName || '',
              wardCode: a.wardCode || '',
              provinceCode: a.provinceCode || '',
              name: a.name || '',
              isDefault: a.isDefault,
              telephone: a.telephone || '',
              isFromApi: true,
              addressNote: a.addressNote || '',
              note: a.addressNote,
            };
          })
          ?.filter(
            (location) =>
              !![
                location.address,
                location.wardName,
                location.provinceName,
              ].join(''),
          )
          ?.sort((a, b) => {
            if (a.addressId === location?.addressId) return -1;
            if (b.addressId === location?.addressId) return 1;
            return 0;
          }),
      [addresses, location],
    ) || [];

  const activeLocation =
    selectedLocation ||
    locations.find((l) => l.addressId === location?.addressId) ||
    locations.find(
      (l) => convertFullAddress(l) === convertFullAddress(location),
    );

  return (
    <>
      <BodyModal>
        <ListLocation>
          {(user?.userId ? loadingAddress : false) ||
          isLoadingGetDistanceTerminals ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 300,
              }}
            >
              <Spinner />
            </div>
          ) : (
            locations.map((location, index) => {
              const isActive =
                !!activeLocation &&
                location.addressId === activeLocation.addressId;
              return (
                <LocationPickerItem
                  onClick={() => setSelectedLocation(location)}
                  telephone={location.telephone}
                  isDefault={!!location.isDefault}
                  name={location.name || ''}
                  address={[
                    location.address,
                    location.wardName,
                    location.provinceName,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                  key={`${location.provinceCode}-${location.wardCode}-${index}`}
                  active={isActive}
                  addressNote={location.addressNote || ''}
                  onEdit={
                    location.addressId
                      ? () => {
                          const rawAddress = addresses.find(
                            (a) => a.addressId === location.addressId,
                          );
                          if (rawAddress) handleEditAddress(rawAddress);
                        }
                      : undefined
                  }
                  onRemove={
                    location.addressId && !location.isDefault
                      ? () => handleRemoveAddress(location.addressId as string)
                      : undefined
                  }
                />
              );
            })
          )}
        </ListLocation>
        <Row
          onClick={() => handleEditAddress()}
          padding="s12"
          marginTop="s8"
          justify="start"
          align="middle"
          style={{ cursor: 'pointer' }}
        >
          <AddAlternativeOutlined size={16} color={theme.colors.primary} />
          <Body style={{ marginLeft: 4 }} color={theme.colors.primary}>
            {t('Thêm địa chỉ')}
          </Body>
        </Row>
      </BodyModal>
      <StyledButtonWrapper>
        <Button
          width="100%"
          variant="primary"
          borderRadius="m"
          disabled={!activeLocation}
          onClick={() =>
            activeLocation &&
            getDistanceTerminalsByAddress({
              address: activeLocation.address || '',
              provinceName: activeLocation.provinceName || '',
              wardName: activeLocation.wardName || '',
              addressNote: activeLocation.note,
              addressId: activeLocation.addressId,
            })
          }
        >
          {t('Xác nhận')}
        </Button>
      </StyledButtonWrapper>
      {messageError && <WrapperError>{messageError}</WrapperError>}
    </>
  );
};

const BodyModal = styled.div`
  margin-bottom: 10px;
  width: 100%;
`;

const ListLocation = styled(Col)`
  overflow-y: auto;
  max-height: 50vh;
`;

export const WrapperError = styled.div<ThemeProps>(
  ({ theme }) => `
  width: 100%;
  border-radius: 8px;
  border: 1px solid ${theme.colors.warning};
  background-color: #FFFBE6;
  padding: 8px;
`,
);

const StyledButtonWrapper = styled(Shape)`
  width: 100%;
  position: fixed;
  bottom: 16px;
  padding-left: 16px;
  padding-right: 16px;
`;
